/* WenStorm analytics — the SHOPIFY adapter.
 *
 * Everything in this file is a fact about THIS storefront: which events the theme emits, which DOM
 * attributes carry durable ids, and which controls are duplicates of each other. None of it is a
 * fact about WenStorm, and none of it is a fact about PostHog.
 *
 * THIS IS THE FILE THAT GETS DELETED when the Shopify frontend goes away. What survives is the
 * event vocabulary in wenstorm-analytics.js, the provider in wenstorm-analytics-posthog.js, the
 * PostHog project and its history, and everything built on top of them. Splitting it out now is
 * what makes that a deletion rather than an excavation.
 *
 * Exposes: window.wenstormInstallShopifyAdapter(analytics)
 *          .classifyClick / .durableId / .namedAction / .isDuplicateControl -- the pure rules,
 *          exported so tests run the real classifier instead of a copy that can drift.
 */
(function (global, doc) {
  'use strict';

  /* ---- Shopify adapter ------------------------------------------------------------------------
   * Translates what THIS storefront happens to emit into the platform-neutral vocabulary.
   *
   * VERIFIED ON THE ACTUAL THEME (2026-09-07), not assumed: Whisper does NOT emit Shopify's newer
   * standard storefront events -- no shopify:product:view, no shopify:cart:lines-update anywhere in
   * its 570 files. Its only `shopify:` listeners are theme-EDITOR events, a different family. What
   * it does emit is its own `cart:update` CustomEvent carrying the cart in detail.resource, from
   * three call sites. So that is what we subscribe to. If the theme later adopts the Shopify
   * standard events, this adapter changes and nothing above it does.
   */
  /* ---- the pure click classifier --------------------------------------------------------------
   * MODULE SCOPE, AND EXPORTED, ON PURPOSE. These four functions used to live inside the installer
   * closure, unreachable from a test, so the regression suite reimplemented them in the test file.
   * Those copies agreed with production line for line on the day they were written and would have
   * gone on passing after production changed underneath them -- a test that cannot fail when the
   * code it guards breaks is worse than no test, because it reports safety. The suite now calls
   * exactly what ships, and there is one copy of the rules.
   *
   * classifyClick() decides WHAT a click is; it emits nothing and knows no analytics object. The
   * listener below does the dispatching. That separation is what makes it callable from a test. */
  /* BOUNDED. An unbounded walk is worse than no walk: on a thread page it climbed out of the
   * clicked link's own card and picked up #fpp-root's data-original-post-id -- the id of the page
   * being VIEWED, not the thread being CLICKED. Observed directly: thread_id 9800013 (current
   * page) reported alongside thread_handle "should-i-install-an-outlet..." (the actual target).
   * A null is a gap you can see; a confidently wrong id is a gap you cannot.
   *
   * So: the element itself, then at most a few ancestors, and never past a page-level container. */
  var PAGE_ROOTS = ['fpp-root', 'fpu-root', 'MainContent'];
  function durableId(el, names) {
    var depth = 0;
    for (var n = el; n && depth < 4; n = n.parentElement, depth++) {
      if (!n.getAttribute) continue;
      // Stop before adopting anything the whole page owns.
      if (n.id && PAGE_ROOTS.indexOf(n.id) >= 0) return null;
      if (n.tagName === 'BODY' || n.tagName === 'MAIN') return null;
      for (var i = 0; i < names.length; i++) {
        var v = n.getAttribute(names[i]);
        if (v) return v;
      }
    }
    return null;
  }

  /* CLASSIFICATION ORDER IS THE WHOLE DESIGN HERE, and both earlier orderings were wrong.
   *
   * Measured on the real theme 2026-09-07:
   *
   *  - Add to cart sits inside <sticky-add-to-cart data-product-id="10330467041569">. Testing
   *    `el.closest('[data-product-id]')` BEFORE named actions therefore classified a genuine
   *    add-to-cart as product_clicked and returned, so action_clicked could never fire.
   *
   *  - On a thread page, #fpp-root carries data-original-post-id, so testing
   *    `el.closest('[data-original-post-id]')` made EVERY control a "thread click": all six
   *    buttons tried -- including "Delete post", "Post Reply" and "Cancel" -- emitted
   *    forum_thread_clicked with null id and null handle. Page identity is not click identity.
   *
   * The rules that follow from that:
   *   1. A NAMED ACTION wins outright. It is the most specific thing we know about the click.
   *   2. NAVIGATION requires a real anchor whose href actually points at the resource. A <button>
   *      does not navigate to a product; treating containers as navigation is what caused both
   *      bugs above.
   *   3. Durable ids are looked up separately and bounded, and are never themselves the trigger.
   */

  /* DUPLICATE CONTROLS: one intent must be one event.
   *
   * This theme renders TWO add-to-cart buttons -- the canonical one inside
   * <add-to-cart-component name="add">, and a second inside <sticky-add-to-cart>. Pressing the
   * STICKY one forwards to the canonical one, so a single human intent produces two clicks and,
   * before this, two action_clicked events. Measured in run #3: four action_clicked against two
   * genuine cart changes (item_count 4 then 5 -- one item added per attempt).
   *
   * Fixed structurally, NOT with a time-based dedupe. The forwarding gap was 59ms in one
   * measurement and 1.0s in the run itself, so any debounce window wide enough to catch it would
   * also swallow a person deliberately clicking twice to add two items -- which is two real
   * intents and must stay two events.
   *
   * Ignoring the sticky control loses nothing: the canonical one fires either way. */
  function isDuplicateControl(el) {
    return !!(el.closest && el.closest('sticky-add-to-cart') && /add to cart/i.test(el.textContent || ''));
  }

  function namedAction(el) {
    var explicit = el.getAttribute('data-analytics-action');
    if (explicit) return explicit;
    if (isDuplicateControl(el)) return null;
    var name = (el.getAttribute('name') || '').toLowerCase();
    var text = (el.textContent || '').trim();
    if (name === 'add' || /add to cart/i.test(text)) return 'add_to_cart';
    if (/check\s?out/i.test(text)) return 'checkout';
    return null;
  }

  /* WHERE ON THE PAGE THE CLICK HAPPENED.
   *
   * "Clicked Forum" is a different fact depending on whether it came from the header nav, the
   * footer, a hero call-to-action, or a card in the body -- one is navigation furniture, another is
   * a piece of merchandising doing its job. Without this the timeline shows the same line for all
   * of them and the difference is unrecoverable.
   *
   * It is captured NOW rather than derived later. PostHog does keep the DOM chain, so in principle
   * header-versus-footer could be dug out of stored strings afterwards -- but that is parsing
   * historical markup with a regex to answer a question we could simply have recorded, and it stops
   * working the moment the theme's classes change. A field written at capture time keeps its
   * meaning for as long as the data lives.
   *
   * The ladder is deliberately short and built on real landmarks that were checked against the
   * live DOM (<header>, <footer>, <main>/#MainContent all exist). An unrecognised place returns
   * null rather than a guess -- the same rule the rest of this system follows. Anything that
   * deserves a name of its own gets data-analytics-location, which wins outright.
   */
  var HERO_HINT = /(^|[\s-])(hero|banner)([\s-]|$)/i;
  function clickLocation(el) {
    if (!el || !el.closest) return null;

    // Explicit beats inferred, always. One attribute on a section is worth any amount of guessing.
    var named = el.closest('[data-analytics-location]');
    if (named) return named.getAttribute('data-analytics-location') || null;

    // Footer first: a <nav> inside the footer is footer navigation, not header navigation, and
    // testing for nav earlier would quietly mislabel every footer menu link.
    if (el.closest('footer')) return 'footer';
    if (el.closest('header')) return 'header_nav';
    if (el.closest('nav')) return 'nav';

    for (var n = el; n; n = n.parentElement) {
      if (n.tagName === 'BODY') break;
      var cls = typeof n.className === 'string' ? n.className : '';
      if (HERO_HINT.test(cls) || (n.id && HERO_HINT.test(n.id))) return 'hero';
    }

    if (el.closest('main, #MainContent')) return 'main_content';
    return null;
  }

  /* The whole decision, in the order the comment above justifies. Returns null when a click is not
   * something we have anything true to say about -- which is most clicks, and is a real answer. */
  function classifyClick(el) {
    if (!el) return null;

    // 1. NAMED ACTIONS FIRST.
    var action = namedAction(el);
    if (action) {
      return { kind: 'action', payload: {
        action: action,
        click_location: clickLocation(el),
        label: (el.textContent || '').trim().slice(0, 60),
        /* A TARGET FINGERPRINT, because an action has no href and no durable id to match against.
         * product_clicked and forum_thread_clicked can be paired with their raw autocapture rows
         * by href or durable id; "Checkout" on the cart page has neither. The tag plus the visible
         * text is what autocapture itself records ($el_text and the tag in elements_chain), so
         * emitting the same two things here gives the normalizer something deterministic to join
         * on instead of falling back to "whatever raw row happened to be nearest in time". */
        target_tag: (el.tagName || '').toLowerCase() || null,
      } };
    }

    // 2. NAVIGATION, and only via an anchor that genuinely points somewhere.
    var anchor = el.closest('a[href]');
    var href = anchor ? (anchor.getAttribute('href') || '') : '';
    if (!href) return null;   // a control with no destination is not navigation, and not our event

    if (/\/products\//.test(href)) {
      return { kind: 'product', payload: {
        click_location: clickLocation(anchor),
        product_id: durableId(anchor, ['data-product-id']),
        product_handle: (href.match(/\/products\/([^/?#]+)/) || [])[1] || null,
        product_title: (anchor.textContent || '').trim().slice(0, 80) || null,
      } };
    }

    if (/\/pages\/threads\//.test(href)) {
      return { kind: 'thread', payload: {
        click_location: clickLocation(anchor),
        // data-forum-id only. data-original-post-id is the VIEWED page's identity and must never
        // be reachable from a click on something else.
        thread_id: durableId(anchor, ['data-forum-id']),
        thread_handle: (href.match(/\/pages\/threads\/([^/?#]+)/) || [])[1] || null,
        thread_title: (anchor.textContent || '').trim().slice(0, 100) || null,
      } };
    }

    /* 4. ORDINARY INTERNAL NAVIGATION -- the fallback, and the reason click_location exists at all.
     *
     * Everything above is a link we had something specific to say about. This is the rest: a Forum
     * link, a collection, a guide, an About page. Without this branch those clicks were only raw
     * autocapture rows, and the raw model deliberately carries no click_location -- so the location
     * was computed, was correct, and was then never asked for. "Clicked Forum" from the header nav
     * and the same link in the footer rendered as one identical line, which is precisely the
     * distinction the field was added to preserve.
     *
     * It stays LAST so it never shadows a more specific classification, and it is internal-only:
     * a fragment, a mailto:, a tel: or another site is not this site's navigation. */
    if (isInternalPath(href)) {
      return { kind: 'navigation', payload: {
        click_location: clickLocation(anchor),
        target_path: pathOf(href),
        target_label: (anchor.textContent || '').trim().slice(0, 80) || null,
      } };
    }
    return null;
  }

  // Same-site, and somewhere a person can actually go. '#' toggles UI, mailto:/tel: leave the web,
  // and another origin is somebody else's site -- none of them are navigation within WenStorm.
  function isInternalPath(href) {
    var h = String(href || '').trim();
    if (!h || h.charAt(0) === '#') return false;
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(h) && !/^https?:/i.test(h)) return false;   // mailto:, tel:, javascript:
    if (/^https?:/i.test(h)) {
      try { return new URL(h).host === global.location.host; } catch (e) { return false; }
    }
    return h.charAt(0) === '/';
  }
  function pathOf(href) {
    var h = String(href || '');
    if (/^https?:/i.test(h)) { try { return new URL(h).pathname; } catch (e) { return null; } }
    return h.split('#')[0].split('?')[0] || null;
  }

  /* CONTROLS THAT ADD TO CART WITHOUT SAYING SO.
   *
   * The landing pages are full of them. sections/landing-hero.liquid renders a CTA whose text is
   * "Shop Now"; it calls preventDefault() and POSTs to /cart/add.js. Nothing about the element says
   * add-to-cart, so the label rules above see an ordinary button and emit nothing -- the visit's
   * most important moment was invisible on the timeline. And this is one page of many: any future
   * CTA that quietly adds to cart would be missed the same way.
   *
   * A LIST OF BUTTON LABELS WOULD BE THE WRONG FIX. It would need editing every time a landing page
   * is written, and the failure mode is silence, so nobody would notice the omission for months.
   *
   * So the CART is the witness. When the cart's contents grow within a moment of a click, that
   * click added to cart -- whatever it called itself. That covers controls not yet written, which a
   * label list cannot.
   *
   * Two things this must not do:
   *   - DOUBLE COUNT. The canonical product-page button already emits add_to_cart by name, so a
   *     click that has already been reported is never reported again.
   *   - MISREAD A REMOVAL. Cart drawers emit the same event when quantity drops, so an increase is
   *     required, and controls that read as removals are ignored outright.
   */
  // MEASURED ON THE REAL PAGE, not guessed. The landing CTA does three sequential requests before
  // the cart event arrives -- resolve the variant from /products/x.js, POST /cart/add.js, then
  // GET /cart.js -- and a real click was observed with 3.0s between the click and the cart change.
  // A 2.5s window silently missed exactly the case this exists to catch. Six seconds covers the
  // round trips with room for a slow connection; a cart change six seconds after an unrelated click
  // is not a plausible coincidence on a page a person is actually using.
  var CART_INFER_WINDOW_MS = 6000;
  function looksLikeRemoval(el) {
    if (!el) return false;
    var t = (el.textContent || '').trim().toLowerCase();
    if (/remove|delete|clear cart|empty/.test(t)) return true;
    return !!(el.closest && el.closest('cart-drawer-component, cart-items-component, [data-cart-remove]'));
  }

  function installShopifyAdapter(analytics) {
    // The last click, and whether it already accounted for itself.
    var lastClick = { el: null, at: 0, named: null, label: null, tag: null };
    // null until the first cart event, because we do not know the cart's size at page load without
    // spending a request on it.
    var lastItemCount = null;

    doc.addEventListener('cart:update', function (e) {
      var cart = e && e.detail && e.detail.resource;
      var count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;

      var recentClick = lastClick.el && (Date.now() - lastClick.at) < CART_INFER_WINDOW_MS;
      var grew = count != null && lastItemCount != null && count > lastItemCount;
      // First cart event of the page: the previous size is unknown, so an increase cannot be
      // proven. A deliberate click moments earlier on something that is plainly not a removal is
      // taken as an add -- which is the landing-page case -- and anything else waits for evidence.
      var firstEventAfterClick = count != null && lastItemCount == null && recentClick
                                 && !looksLikeRemoval(lastClick.el);

      if (recentClick && lastClick.named !== 'add_to_cart' && (grew || firstEventAfterClick)) {
        analytics.actionClicked({
          action: 'add_to_cart',
          label: lastClick.label,
          target_tag: lastClick.tag,
          click_location: lastClick.location,
          // How we know. This one was not announced by the control; it was deduced from the cart
          // changing right after it was clicked, and the timeline should be able to say so.
          detected_via: 'cart_change',
        });
        lastClick.named = 'add_to_cart';   // never reported twice for one click
      }

      if (count != null) lastItemCount = count;
      analytics.cartUpdated({ item_count: count });
    });

    /* Delegated click capture. One listener, not a hook per section -- Shopify's editor re-renders
     * sections without a page load, so anything bound per-section silently stops working.
     *
     * IDENTITY IS RESOLVED SEPARATELY FROM THE LINK. An earlier version used one combined selector,
     *   el.closest('[data-original-post-id], a[href*="/pages/threads/"]')
     * which returns whichever ancestor matches FIRST -- the anchor -- and then read the id attribute
     * off it, yielding null every time the id lived anywhere else. Two lookups, never one.
     *
     * The attribute names were also wrong, which the combined selector hid. Verified against the
     * live DOM 2026-09-07: forum cards carry `data-forum-id` ON THE ANCHOR (9800013, matching the
     * thread page's own #fpp-root), NOT `data-original-post-id` -- that one exists only on the
     * thread page root. Product links carry no id at all; the handle in the href is the only
     * client-side identifier, and the numeric product id is resolvable from it server-side.
     */
    doc.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('a, button, [data-analytics-action]') : null;
      var c = classifyClick(el);

      // Remembered whether or not it classified, because the cart may be about to reveal what it
      // was. A click the rules had nothing to say about is exactly the case this catches.
      if (el) {
        lastClick = {
          el: el, at: Date.now(),
          named: c && c.kind === 'action' ? c.payload.action : null,
          label: (el.textContent || '').trim().slice(0, 60) || null,
          tag: (el.tagName || '').toLowerCase() || null,
          location: clickLocation(el),
        };
      }

      if (!c) return;
      if (c.kind === 'action') analytics.actionClicked(c.payload);
      else if (c.kind === 'product') analytics.productClicked(c.payload);
      else if (c.kind === 'thread') analytics.forumThreadClicked(c.payload);
      else if (c.kind === 'navigation') analytics.navigationClicked(c.payload);
    }, true);
  }

  // Exported so the regression suite exercises the shipping rules rather than a copy of them.
  installShopifyAdapter.classifyClick = classifyClick;
  installShopifyAdapter.clickLocation = clickLocation;
  installShopifyAdapter.durableId = durableId;
  installShopifyAdapter.namedAction = namedAction;
  installShopifyAdapter.isDuplicateControl = isDuplicateControl;

  global.wenstormInstallShopifyAdapter = installShopifyAdapter;
})(window, document);
